import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocalStorage } from '@rehooks/local-storage';
import {
  Box,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import Popper from '@mui/material/Popper';
import { usePublisherCommon } from '../hooks/publisher-common.hook';
import { setPublisherName, setPublisherId as setPublisherIdAction } from '../context';

const FullWidthPopper = (props) => {
  const width = props?.anchorEl?.clientWidth ?? undefined;
  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width,
      }}
      placement="bottom-start"
    />
  );
}

function PublisherSelect() {
  const {user} = useSelector((state) => state.auth);  

  const [publisherName, setPublisherNameLocal] = useLocalStorage(`sooth-publisherName`, 'The Economist');
  const [storedPublisherId, setStoredPublisherId] = useLocalStorage(`sooth-publisherId`, 1122);

  const { publisherDetails, publisherId, roles } = useSelector((state) => state.publisher);
  const isAdmin = roles?.includes('Admin');
  
  const dispatch = useDispatch();

  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const previousInputValue = useRef('');
  const userIsTyping = useRef(false);
  const inputRef = useRef(null);
  const hasUserInteracted = useRef(false);

  const { publisherList, setQueryString, getPublisherList } =
    usePublisherCommon();

  const initialOption = React.useMemo(() => {
    return publisherName && publisherId
      ? { sourcesId: publisherId, entityName: publisherName }
      : null;
  }, [publisherId, publisherName]);

  const options = React.useMemo(() => {
    const base = Array.isArray(publisherList) ? publisherList : [];
    const merged = !userIsTyping.current && initialOption
      ? [initialOption, ...base]
      : base;
    const seen = new Set();
    return merged.filter((opt) => {
      const key = opt?.sourcesId ?? opt?.entityName;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [publisherList, initialOption]);

  const handlePublisherSelect = (_, newValue) => {
    userIsTyping.current = false;
    setSelectedValue(newValue);
    setInputValue(newValue?.entityName ?? '');
    const newId = newValue?.sourcesId ?? null;
    dispatch(setPublisherIdAction(newId));
    setStoredPublisherId(newId);
      setPublisherNameLocal(newValue?.entityName ?? '');
      dispatch(setPublisherName(newValue?.entityName ?? ''));
      setForceOpen(false);
  };

  const handleInputChange = (_, newInputValue) => {
    hasUserInteracted.current = true;
    userIsTyping.current = true;
    setInputValue(newInputValue);
    if (newInputValue !== (selectedValue?.entityName || '')) {
      setSelectedValue(null);
    }
    if (newInputValue.length >= 2) {
      setForceOpen(true);
    } else if (newInputValue.length === 0) {
      setForceOpen(false);
    }
  };

  useEffect(() => {
    if (forceOpen && hasUserInteracted.current && inputRef.current) {
      requestAnimationFrame(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus();
          const value = inputRef.current.value ?? '';
          inputRef.current.setSelectionRange(value.length, value.length);
        }
      });
    }
  }, [forceOpen]);

  const handleNonAdminPublisherChange = (e) => {
    const selectedId = e.target.value;
    const selectedPublisher = publisherDetails.find(p => 
      p.publisherId === selectedId || p.sourcesId === selectedId
    );
    dispatch(setPublisherIdAction(selectedId));
    setStoredPublisherId(selectedId);
    const publisherNameToSet = selectedPublisher?.publisherName || selectedPublisher?.entityName || '';
    setPublisherNameLocal(publisherNameToSet);
    dispatch(setPublisherName(publisherNameToSet));
  };

  useEffect(() => {
    if (isAdmin && !userIsTyping.current) {
      if (initialOption) {
        setSelectedValue(initialOption);
        setInputValue(initialOption.entityName);
      } else {
        setSelectedValue(null);
        setInputValue('');
      }
    }
  }, [initialOption, isAdmin]);

  useEffect(() => {
    dispatch(setPublisherName(publisherName));
    if (storedPublisherId !== null && storedPublisherId !== publisherId) {
      dispatch(setPublisherIdAction(storedPublisherId));
    }
  }, [publisherName, storedPublisherId, publisherId, dispatch]);

  useEffect(() => {
    if (!isAdmin || inputValue === previousInputValue.current) return;
    previousInputValue.current = inputValue;
    if (inputValue.length < 2) {
      setLoading(false);
      setForceOpen(false);
      if (inputValue.length === 0) {
        setQueryString('');
      }
      return;
    }
    if (!hasUserInteracted.current) return;
    const timeoutId = setTimeout(async () => {
      setLoading(true);
      setForceOpen(true);
      setQueryString(inputValue);
      try {
        await getPublisherList(inputValue);
        setTimeout(() => {
          setForceOpen(true);
          if (inputRef.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
            const value = inputRef.current.value ?? '';
            inputRef.current.setSelectionRange(value.length, value.length);
          }
        }, 0);
      } catch (err) {
        console.error('Error fetching publishers:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue, setQueryString, getPublisherList, isAdmin]);

  if (!isAdmin) {
    if (Array.isArray(publisherDetails) && publisherDetails.length > 1) {
      return (
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Publisher</InputLabel>
          <Select
            value={publisherId || ''}
            label="Publisher"
            onChange={handleNonAdminPublisherChange}
          >
            {publisherDetails.map((publisher) => (
              <MenuItem key={publisher.publisherId || publisher.sourcesId} value={publisher.publisherId || publisher.sourcesId}>
                {publisher.publisherName || publisher.entityName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    
    return (
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          whiteSpace: 'nowrap',
        }}
      >
        {publisherName || 'Publisher'}
      </Typography>
    );
  }

  return (
    <Autocomplete
      sx={{ minWidth: 250 }}
      options={options}
      filterOptions={(opts) => opts}
      getOptionLabel={(option) => option?.entityName || ''}
      isOptionEqualToValue={(opt, val) => (opt?.sourcesId ?? opt?.entityName) === (val?.sourcesId ?? val?.entityName)}
      blurOnSelect={false}
      clearOnBlur={false}
      disableCloseOnBlur={true}
      disableCloseOnSelect
      open={forceOpen}
      onClose={(event, reason) => {
        if (reason === "selectOption") return;
        setForceOpen(false);
      }}
      slots={{ popper: FullWidthPopper }}
      value={selectedValue}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handlePublisherSelect}
      noOptionsText={
        inputValue.length < 2
          ? 'Type at least 2 characters'
          : 'No publishers found'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={inputValue ? '' : 'Search Publisher'}
          placeholder={
            inputValue.length === 0 ? 'Type at least 2 characters' : ''
          }
          sx={{
            '& .MuiInputBase-root': { height: 56 },
            '& .MuiInputLabel-root': {
              top: '50%',
              left: 14,
              transform: 'translateY(-50%)',
            },
            '& .MuiInputLabel-shrink': {
              top: 0,
              left: 14,
              transform: 'translateY(-50%) scale(0.75)',
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box
          {...props}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start !important',
            padding: '8px',
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {option.entityName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {option.corpusUrl || 'No URL available'}
          </Typography>
        </Box>
      )}
    />
  );
}

export default PublisherSelect;
